import { Panel } from "@/components/common";
import { LineChart } from "@/components/capital/LineChart";

function Stat({ label, value, testId, tone }) {
  return (
    <div className="rounded-md border border-[var(--border-subtle)] px-3 py-2" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-mono text-sm ${tone || "text-slate-200"}`}>{value ?? "—"}</div>
    </div>
  );
}

const tone = (v) => (v == null ? "" : Number(v) >= 0 ? "text-emerald-400" : "text-rose-400");

export function BookSummary({ r }) {
  const perf = r.performance || {};
  return (
    <Panel title={`${r.label}${r.is_simulation ? " · simulation" : ""}`} testId={`book-summary-${r.book}`}>
      <div className="p-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        <Stat label="Equity" value={`${r.equity} ${r.ccy}`} testId="cap-equity" />
        <Stat label="Capital (flows)" value={r.capital} testId="cap-capital" />
        <Stat label="Eligible (compounded)" value={r.eligible_capital} testId="cap-eligible" tone={r.negative_capital ? "text-rose-400" : ""} />
        <Stat label="Realized P&L" value={r.realized_pnl} testId="cap-realized" tone={tone(r.realized_pnl)} />
        <Stat label="Unrealized P&L" value={r.unrealized_pnl} testId="cap-unrealized" tone={tone(r.unrealized_pnl)} />
        <Stat label="Fees" value={r.fees ?? "—"} testId="cap-fees" />
        <Stat label="Net P&L" value={r.net_pnl} testId="cap-net" tone={tone(r.net_pnl)} />
        <Stat label="Cash" value={r.cash} testId="cap-cash" />
        {r.book === "real" ? <Stat label="Return on contributed %" value={r.return_on_contributed_pct} testId="cap-twr" />
          : <Stat label="Time-weighted return %" value={perf.twr_pct} testId="cap-twr" />}
        <Stat label="Max drawdown %" value={perf.max_drawdown_pct ?? r.drawdown_pct} testId="cap-mdd" />
        <Stat label="Sharpe / Sortino" value={perf.sharpe != null ? `${perf.sharpe} / ${perf.sortino ?? "—"}` : "—"} testId="cap-sharpe" />
        <Stat label="Reconciliation" value={r.reconciliation} testId="cap-recon" tone={r.reconciliation === "reconciled" ? "text-emerald-400" : "text-amber-400"} />
      </div>
      {r.negative_capital && <div data-testid="cap-negative" className="px-4 pb-3 text-xs text-rose-400">Eligible capital is negative ({r.raw}); allocation is paused.</div>}
      {r.equity_curve && <div className="px-4 pb-4"><LineChart testId="cap-equity-curve" points={r.equity_curve.map((p) => ({ t: p.t, v: p.equity }))} /></div>}
      {r.capital_timeline && <CapitalTimeline r={r} />}
    </Panel>
  );
}

function CapitalTimeline({ r }) {
  return (
    <div className="px-4 pb-4 space-y-2 text-xs">
      <LineChart testId="cap-contributed-curve" color="#F59E0B" points={r.capital_timeline.map((p) => ({ t: p.t, v: p.contributed }))} />
      <div className="text-slate-400" data-testid="cap-flow-summary">Deposits {r.external_deposits} · withdrawals {r.personal_withdrawals} · pending withdrawals {r.pending_withdrawals} (reserved) · internal transfers {r.internal_transfers_excluded} (excluded, not P&L)</div>
      {r.flow_checks.map((c) => (
        <div key={c.check} data-testid={`flow-check-${c.check}`} className={c.ok ? "text-emerald-400" : "text-rose-400"}>{c.ok ? "PASS" : "FAIL"} {c.check.replaceAll("_", " ")}</div>))}
      <div className="text-slate-500">{r.note}</div>
    </div>
  );
}
